import { useState } from "react";
import { Alert, Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { ArrowForward as ArrowIcon } from "@mui/icons-material";
import type {
  ChangeRequestStatus,
  ChangeRequestView,
  EditFieldGovernance,
} from "../types";
import {
  APPROVER_DISPLAY,
  FIELD_LABELS,
  isDocumentField,
  stringifyValue,
} from "./changeRequestFormat";
import { DocumentDiffCell, parseDocumentValue } from "./DocumentDiffCell";

function extractFieldValue(bag: unknown, field: string): unknown {
  if (bag == null) return null;
  if (typeof bag === "object" && !Array.isArray(bag)) {
    const obj = bag as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(obj, field)) return obj[field];
  }
  return bag;
}

function ValueCell({
  field,
  value,
  strikethrough,
  onPreviewError,
}: {
  field: string;
  value: unknown;
  strikethrough?: boolean;
  onPreviewError?: (message: string) => void;
}) {
  if (isDocumentField(field)) {
    return (
      <DocumentDiffCell
        doc={parseDocumentValue(value)}
        strikethrough={strikethrough}
        onError={onPreviewError}
      />
    );
  }
  return (
    <Box
      component="span"
      sx={{
        textDecoration: strikethrough ? "line-through" : undefined,
        color: strikethrough ? "text.secondary" : undefined,
        fontWeight: strikethrough ? undefined : 500,
      }}
    >
      {stringifyValue(value)}
    </Box>
  );
}

const STATUS_COLOR: Record<
  ChangeRequestStatus,
  "success" | "warning" | "error" | "default"
> = {
  APPLIED: "success",
  PENDING: "warning",
  REJECTED: "error",
  CANCELLED: "default",
};

const STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  APPLIED: "APPLIED",
  PENDING: "PENDING APPROVAL",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
};

const FIELD_APPROVER_OVERRIDE: Record<string, string> = {
  credit_days: "Procurement + Finance",
  payment_terms_pattern: "Procurement + Finance",
  vendor_type: "Compliance",
  gst_certificate: "Compliance",
  legal_name: "Compliance",
  pan_number: "Compliance",
  pan_card_attachment: "Compliance",
};

function approverDisplayFor(
  cr: ChangeRequestView,
  governance: Record<string, EditFieldGovernance>,
): string {
  for (const f of cr.fields ?? []) {
    if (FIELD_APPROVER_OVERRIDE[f]) return FIELD_APPROVER_OVERRIDE[f];
  }
  for (const f of cr.fields ?? []) {
    const role = governance[f]?.approver_role;
    if (role && APPROVER_DISPLAY[role]) return APPROVER_DISPLAY[role];
  }
  return "reviewer";
}

// Preference-ordered list; vendor_type must win over gst/gst_certificate/
// billing_state in a vendor_type bundle, but a GST-only bundle (no vendor_type)
// still gets a meaningful headline via gst / gst_certificate.
const PRIMARY_FIELD_PRIORITY: string[] = [
  "vendor_type",
  "pan_number",
  "email_id",
  "poc_contact",
  "po_notification_emails",
  "escalation_emails",
  "vendor_invoice_portal_access_email",
  "tars_production_process_management_access_email",
  "address_billing",
  "location_pincode",
  "account_number",
  "ifsc_code",
  "msme_certificate",
  "vendor_profile_type",
  "super_category",
  "sub_category",
  "credit_days",
  "payment_terms_pattern",
  "legal_name",
  "gst",
  "gst_certificate",
];

function labelFor(fields: string[] | undefined): string {
  if (!fields || fields.length === 0) return "—";
  const chosen =
    PRIMARY_FIELD_PRIORITY.find((f) => fields.includes(f)) ?? fields[0];
  return FIELD_LABELS[chosen] ?? chosen;
}

function formatLoggedAt(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  return `${date}, ${time}`;
}

type Props = {
  changeRequests: ChangeRequestView[];
  governance?: Record<string, EditFieldGovernance>;
};

export function ChangeRequestsPanel({
  changeRequests,
  governance = {},
}: Props) {
  const [previewError, setPreviewError] = useState<string | null>(null);
  if (changeRequests.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          border: "1px dashed",
          borderColor: "#B7BFD1",
          bgcolor: "transparent",
          borderRadius: 1,
          py: 6,
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: "text.secondary" }}>
          No change requests yet. Edit a field from the Profile tab to raise one.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {previewError && (
        <Alert
          severity="error"
          onClose={() => setPreviewError(null)}
          sx={{ mb: 0 }}
        >
          {previewError}
        </Alert>
      )}
      {changeRequests.map((cr) => (
        <Paper
          key={cr.code}
          variant="outlined"
          sx={{ borderColor: "#E0E4EF", borderRadius: 1, p: 2.5 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, minWidth: 0 }}>
              <Typography
                sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}
              >
                {cr.code}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {labelFor(cr.fields)}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={STATUS_LABEL[cr.status] ?? "UNKNOWN"}
              color={STATUS_COLOR[cr.status] ?? "default"}
              sx={{ fontWeight: 700, letterSpacing: 0.6 }}
            />
          </Box>

          {(() => {
            const fields = cr.fields ?? [];
            if (fields.length > 1) {
              return (
                <Stack spacing={0.75} mt={1.5}>
                  {fields.map((field) => (
                    <Box
                      key={field}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: 13,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 160,
                          fontSize: 12,
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        {FIELD_LABELS[field] ?? field}
                      </Typography>
                      <ValueCell
                        field={field}
                        value={extractFieldValue(cr.old_value, field)}
                        strikethrough
                        onPreviewError={setPreviewError}
                      />
                      <ArrowIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <ValueCell
                        field={field}
                        value={extractFieldValue(cr.new_value, field)}
                        onPreviewError={setPreviewError}
                      />
                    </Box>
                  ))}
                </Stack>
              );
            }
            const single = fields[0];
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                  fontSize: 13,
                  flexWrap: "wrap",
                }}
              >
                <ValueCell
                  field={single ?? ""}
                  value={
                    single ? extractFieldValue(cr.old_value, single) : cr.old_value
                  }
                  strikethrough
                  onPreviewError={setPreviewError}
                />
                <ArrowIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <ValueCell
                  field={single ?? ""}
                  value={
                    single ? extractFieldValue(cr.new_value, single) : cr.new_value
                  }
                  onPreviewError={setPreviewError}
                />
              </Box>
            );
          })()}

          {cr.status === "REJECTED" && cr.review_remarks && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1,
                bgcolor: "#FDECEA",
                border: 1,
                borderColor: "#F5C2C0",
              }}
            >
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: "#7F1D1D",
                  mb: 0.5,
                }}
              >
                REVIEWER REMARKS
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  color: "#7F1D1D",
                  whiteSpace: "pre-wrap",
                }}
              >
                {cr.review_remarks}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mt: 1.5,
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            <Box>
              {cr.status === "PENDING"
                ? `Awaiting review by ${approverDisplayFor(cr, governance)}`
                : ""}
            </Box>
            <Box>Logged · {formatLoggedAt(cr.logged_at)}</Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

export default ChangeRequestsPanel;
